import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@platform/core/lib/format';
import { routes } from '@platform/core/lib/routes';
import { useSince } from '@platform/features/users/inbox/useSince';
import { WorkspaceLink, workspaceType } from '@platform/navigation';
import { useMeQuery, useObserverOfficesQuery } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { initials } from '../../shell/initials';
import { SysNote } from '../system/SysNote';
import { SysTable } from '../system/SysTable';
import { SystemPage } from '../system/SystemPage';
import { accountErrorKey } from './accountErrorKey';
import { useOfficeActions } from './useOfficeActions';
import './admin.css';

const COLUMNS = 'minmax(280px, 1fr) 110px 120px 64px 60px 130px 150px';

/**
 * «Демо-офіси спостерігача» (рішення 5 етапу 7, ADR-024; таблиця — як «Робочі простори»): офіс — плитка типу mp, особа (перехід до офісу) і
 * назва офісу під нею, коли створено й остання сесія (відносний час, повний — у підказці), записи, відкриті сесії, коли фонове завдання видалить
 * офіс (поки є відкрита сесія — «використовується», час — у підказці), дії «Скинути» і «Видалити». Лише адміністратор платформи; права перевіряє
 * сервер.
 */
export function AdminObserverOfficesPage() {
  const { t } = useTranslation();
  const since = useSince();
  const lang = useAppSelector(selectLang);
  const { data: me } = useMeQuery();
  const admin = me?.globalRole === 'Platform admin';
  const { data, error } = useObserverOfficesQuery(undefined, { skip: !admin });
  const { busy, reset, remove } = useOfficeActions();
  const tone = workspaceType('mp').tone;
  const rows = (data?.items ?? []).map((o) => ({
    key: o.workspaceId,
    cells: [
      <span key="o" className="sp-who"><span className="sp-tile" style={{ background: tone }}>{initials(o.personName)}</span>
        <span className="ad-office"><WorkspaceLink className="sp-strong ad-ellipsis" ws={o.workspaceId} to={routes.home()}>{o.personName}</WorkspaceLink><span className="ad-office__name ad-ellipsis" title={o.name}>{o.name}</span></span></span>,
      <span key="c" className="sp-muted" title={formatDateTime(o.createdAt, lang)}>{since(o.createdAt)}</span>,
      <span key="l" className="sp-muted" title={o.lastSessionAt ? formatDateTime(o.lastSessionAt, lang) : undefined}>{o.lastSessionAt ? since(o.lastSessionAt) : '—'}</span>,
      <span key="r" className="sp-mono">{o.records}</span>,
      <span key="s" className="sp-mono">{o.activeSessions}</span>,
      o.activeSessions > 0
        ? <span key="e" className="ad-state is-on" title={o.expiresAt ? formatDateTime(o.expiresAt, lang) : undefined}><span className="ad-state__dot" aria-hidden="true" />{t('sysAccounts.offices.inUse')}</span>
        : <span key="e" className="sp-muted">{o.expiresAt ? formatDateTime(o.expiresAt, lang) : '—'}</span>,
      <span key="a" className="sp-actions">
        <button type="button" className="ad-rowbtn" disabled={busy === o.workspaceId} aria-label={t('sysAccounts.offices.resetOf', { name: o.name })} onClick={() => reset(o)}>{t('sysAccounts.offices.reset')}</button>
        <button type="button" className="ad-rowbtn ad-rowbtn--danger" disabled={busy === o.workspaceId} aria-label={t('sysAccounts.offices.deleteOf', { name: o.name })} onClick={() => remove(o)}>{t('sysAccounts.offices.delete')}</button>
      </span>,
    ],
  }));
  return (
    <SystemPage title={t('sysPages.sections.observer-offices')} sub={t('sysAccounts.offices.sub', { hours: data?.ttlHours ?? 4 })}>
      {me && !admin && <SysNote tone="alert">{t('sysAccounts.offices.locked')}</SysNote>}
      {admin && error && <SysNote tone="alert">{t(accountErrorKey(error))}</SysNote>}
      {admin && (
        <div className="ad-offices">
          <SysTable columns={COLUMNS} minWidth={900} rows={rows} empty={data ? t('sysAccounts.offices.none') : undefined} label={t('sysPages.sections.observer-offices')}
            head={[t('sysAccounts.offices.office'), t('sysAccounts.offices.created'), t('sysAccounts.offices.lastSession'),
              t('sysAccounts.offices.records'), t('sysAccounts.offices.sessions'), t('sysAccounts.offices.expires'), '']} />
        </div>
      )}
    </SystemPage>
  );
}
